"use client"

import { useMemo } from "react"
import { AssistantTopBarButton } from '@/components/ai-chat/AssistantTopBarButton'
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Star, User, LogOut, HelpCircle, Menu, MoreHorizontal } from "lucide-react"
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setActiveTab } from "@/store/slices/navigationSlice"
import { useAuthContext } from "@/contexts/AuthContext"
import { NotificationBellWebComponent } from '@/components/notifications'
import { WatchlistDropdown } from '@/components/watchlist/WatchlistDropdown'
import { ReminderBell, useReminderBadgeCount } from '@/features/reminders/ReminderBell'
import { HeaderTitle } from '@/components/header-title'
import { buildHelpCenterUrl } from '@/config/helpDocs'
import { TopBarMoreMenu } from '@/components/top-bar-more-menu'
import { CreateMenu, CreateMenuHost, TASK_ITEM } from '@/components/create-menu'
import type { CreateMenuItem } from '@/components/create-menu'
import { useCreateFlyoutDeepLink } from '@/components/create-menu/useCreateFlyoutDeepLink'
import { findCreateItemById } from '@/components/create-menu/createRegistry'
import { usePermission } from '@/hooks/usePermission'
import { useSidebar } from '@/components/ui/sidebar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { UserAvatar } from '@/components/user/UserAvatar'
import { GlobalSearch } from '@/components/global-search/GlobalSearch'
import { ChatTopBarButton } from '@/features/chat/components/ChatTopBarButton'
import { cn } from '@/lib/utils'

export function TopBar() {
  const { t } = useTranslation("nav")
  // 手机：侧栏是抽屉，入口在顶栏左侧（见 app-sidebar.tsx 的 isMobile 分支）
  const { isMobile, setOpenMobile } = useSidebar()
  // 手机上次要入口收进「更多」，提醒未读在「⋯」上以红点提示
  // （公告 R-044 之后并进通知铃的第三个页签，通知铃本身在手机上也留在顶栏，不用再计一次）
  const moreHasUnread = useReminderBadgeCount() > 0
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { signOut } = useAuthContext()
  const { userId, fullName, email, avatarUrl, roleCodes, isSuperuser } = useAppSelector((state) => state.user)

  const deepLink = useCreateFlyoutDeepLink()
  const { hasPermission } = usePermission()

  const activeItem = useMemo<CreateMenuItem | null>(() => {
    const kind = deepLink.activeKind
    if (!kind) return null
    const item = findCreateItemById(kind) ?? (kind === TASK_ITEM.id ? TASK_ITEM : undefined)
    if (!item || item.kind === 'route') return null
    if (item.perm && !hasPermission(item.perm)) return null
    return item
  }, [deepLink.activeKind, hasPermission])

  const handleNavigation = (url: string, key: string) => {
    try {
      console.log('Navigating to:', url, 'with key:', key)
      console.log(avatarUrl)
      dispatch(setActiveTab(key))
      void navigate(url)
    } catch (error) {
      console.error('Navigation error:', error)
    }
  }

  const handleLogout = async () => {
    try {
      console.log('🚪 TopBar: 开始登出...')

      // 调用统一的退出逻辑（AuthContext.signOut）
      // 这会依次执行：
      // 1. 调用后端 API 清除 session
      // 2. 清除 Redux 状态和菜单权限
      // 3. 清除 Clerk session
      // 4. 更新本地状态
      await signOut()
      console.log('✅ TopBar: 登出成功')

      // 跳转到登录页
      void navigate('/login')
    } catch (error) {
      console.error('❌ TopBar: 登出失败:', error)

      // 即使失败也跳转到登录页（状态已被清除）
      void navigate('/login')
    }
  }

  const handleCreateSelect = (item: CreateMenuItem) => {
    if (item.kind === 'route' && item.route) {
      void navigate(item.route)
      return
    }
    deepLink.open(item.id)
  }

  const handleCloseCreate = () => {
    deepLink.close()
  }


  // R-044 顶栏收敛：桌面顺序 Create → AI → Watchlist → Reminder → Notification → Help → ⋯。
  // 手机放不下这么多，次要的四个（Watchlist / Reminder / Help / 溢出菜单）收进「更多」面板，
  // 组件原样复用，各自的下拉与未读角标照常工作。
  const secondaryActions = (
    <>
      {/* Watchlist Dropdown */}
      <WatchlistDropdown />

      {/* Reminders */}
      <ReminderBell />

      {/* Help Center — opens the static help site in a new tab */}
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent"
        onClick={() => window.open(buildHelpCenterUrl(roleCodes, isSuperuser), '_blank', 'noopener,noreferrer')}
        title={t("topbar.helpCenter")}
      >
        <HelpCircle className="h-4 w-4" />
        <span className="sr-only">{t("topbar.helpCenter")}</span>
      </Button>

      {/* 低频功能的溢出菜单 */}
      <TopBarMoreMenu userId={userId} userName={fullName} />
    </>
  )

  return (
    <header className="sticky top-0 w-full border-b border-border bg-background shadow-sm">
      <div className="flex h-16 items-center justify-between px-3 md:px-6">
        {/* Left side - Current Route */}
        {/* 桌面：左右两侧等宽（basis-0），全局搜索才落在正中间 */}
        <div className={cn("flex min-w-0 flex-1 items-center gap-2 md:gap-4", isMobile ? "max-w-md" : "basis-0")}>
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent"
              aria-label={t("sidebar.openMenu")}
              onClick={() => setOpenMobile(true)}
              data-testid="mobile-nav-open"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <HeaderTitle />
        </div>

        {/* 全局搜索（⌘K）：桌面在顶栏中间，窄屏收成右侧的放大镜 */}
        {!isMobile && (
          <div className="mx-4 flex w-full min-w-0 max-w-md">
            <GlobalSearch />
          </div>
        )}

        {/* Right side - Actions */}
        <div className={cn("flex items-center gap-1 md:gap-2", isMobile ? "shrink-0" : "flex-1 basis-0 justify-end")}>
          {isMobile && <GlobalSearch compact />}

          {/* Global Create Menu */}
          <CreateMenu onSelect={handleCreateSelect} />

          {/* AI 助手入口（F3：替换原右下角悬浮球；非模态浮窗） */}
          <AssistantTopBarButton />

          {/* Messages（内部即时消息）：有 chat.use 才显示，带未读数 */}
          <ChatTopBarButton />

          {/* Notifications —— Inbox / Archived / Announcements 三个页签
              (公告入口 R-044 并进来了，顶栏不再有独立的 Megaphone) */}
          <NotificationBellWebComponent />

          {isMobile ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={moreHasUnread ? t("topbar.mobileMoreUnread") : t("topbar.mobileMore")}
                  data-testid="topbar-more"
                  className="relative h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent"
                >
                  <MoreHorizontal className="h-5 w-5" />
                  {moreHasUnread && (
                    <span data-testid="topbar-more-dot" aria-hidden="true" className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-2" data-testid="topbar-more-panel">
                <div className="flex flex-wrap items-center gap-1">{secondaryActions}</div>
              </PopoverContent>
            </Popover>
          ) : (
            secondaryActions
          )}

          {/* User Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 w-9 rounded-full p-0">
                <UserAvatar
                  src={avatarUrl}
                  name={fullName}
                  alt={t("topbar.profile")}
                  className="h-9 w-9"
                />
                <span className="sr-only">{t("topbar.userMenu")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background border-border">
              <DropdownMenuLabel className="text-foreground">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium text-foreground">{fullName || t("topbar.userFallback")}</p>
                  <p className="text-xs text-muted-foreground">{email || t("topbar.noEmail")}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem
                className="text-foreground hover:bg-accent hover:text-accent-foreground cursor-pointer"
                onClick={() => handleNavigation('/profile', 'profile')}
              >
                <User className="mr-2 h-4 w-4" />
                {t("topbar.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-foreground hover:bg-accent hover:text-accent-foreground">
                <Star className="mr-2 h-4 w-4" />
                {t("topbar.favorites")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem
                className="text-destructive hover:bg-accent hover:text-destructive cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("topbar.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Global Create Drawer/Flyout Host */}
      <CreateMenuHost
        activeItem={activeItem}
        onClose={handleCloseCreate}
      />
    </header>
  )
}
